import type { CandleInterval, GatewayStatusDto, ServerWsMessage } from "../shared/contracts.ts";
import { useTerminalStore } from "./store.ts";
import { watchlistSignature } from "./watchlist.ts";

const RETRY_DELAYS = [1_000, 2_000, 5_000, 10_000, 30_000] as const;

function staleStatus(state: GatewayStatusDto["state"]): GatewayStatusDto {
  const previous = useTerminalStore.getState().status;
  return {
    ...previous,
    state,
    updatedAt: new Date().toISOString(),
  };
}

export function connectRealtime(): () => void {
  let attempt = 0;
  let disposed = false;
  let socket: WebSocket | undefined;
  let timer: number | undefined;

  const connect = () => {
    if (disposed) return;
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    socket = new WebSocket(`${protocol}//${location.host}/ws`);

    socket.addEventListener("open", () => {
      attempt = 0;
      const state = useTerminalStore.getState();
      if (state.selectedInstrument) {
        socket?.send(JSON.stringify({
          type: "selection.set",
          instrumentUid: state.selectedInstrument.uid,
          interval: state.interval,
        }));
      }
      sendWatchlist(socket, state.watchlist.map((instrument) => instrument.uid));
    });

    socket.addEventListener("message", (event) => {
      try {
        const message = JSON.parse(String(event.data)) as ServerWsMessage;
        useTerminalStore.getState().applyRealtime(message);
      } catch {
        // Invalid browser frames are ignored; the next valid snapshot is authoritative.
      }
    });

    socket.addEventListener("close", () => {
      if (disposed) return;
      useTerminalStore.getState().setStatus(staleStatus("reconnecting"));
      const delay = RETRY_DELAYS[Math.min(attempt, RETRY_DELAYS.length - 1)] ?? 30_000;
      attempt += 1;
      timer = window.setTimeout(connect, delay);
    });
  };

  const unsubscribe = useTerminalStore.subscribe((state, previous) => {
    const uid = state.selectedInstrument?.uid;
    if (uid && (uid !== previous.selectedInstrument?.uid || state.interval !== previous.interval)) {
      sendSelection(socket, uid, state.interval);
    }
    if (watchlistSignature(state.watchlist) !== watchlistSignature(previous.watchlist)) {
      sendWatchlist(socket, state.watchlist.map((instrument) => instrument.uid));
    }
  });

  connect();
  return () => {
    disposed = true;
    unsubscribe();
    if (timer !== undefined) clearTimeout(timer);
    socket?.close(1000, "unmount");
  };
}

function sendWatchlist(socket: WebSocket | undefined, instrumentUids: string[]): void {
  if (socket?.readyState !== WebSocket.OPEN) return;
  socket.send(JSON.stringify({ type: "watchlist.set", instrumentUids }));
}

function sendSelection(
  socket: WebSocket | undefined,
  instrumentUid: string,
  interval: CandleInterval,
): void {
  if (socket?.readyState !== WebSocket.OPEN) return;
  socket.send(JSON.stringify({ type: "selection.set", instrumentUid, interval }));
}
